import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CustomerMaster() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState({
    customer_id: '',
    customer_name: ''
  });

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/masters/customers`, {
        
      });
      setCustomers(response.data);
    } catch (err) { console.error(err);
      toast.error('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        created_at: new Date().toISOString()
      };

      if (editingCustomer) {
        await axios.put(`${API}/masters/customers/${editingCustomer.customer_id}`, data, {
          
        });
        toast.success('Customer updated successfully');
      } else {
        await axios.post(`${API}/masters/customers`, data, {
          
        });
        toast.success('Customer created successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save customer');
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      customer_id: customer.customer_id,
      customer_name: customer.customer_name
    });
    setDialogOpen(true);
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;

    try {
      await axios.delete(`${API}/masters/customers/${customerId}`, {
        
      });
      toast.success('Customer deleted successfully');
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      customer_name: ''
    });
    setEditingCustomer(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}} data-testid="customer-master-title">CUSTOMER MASTER</h1>
          <p className="text-sm text-gray-700 mt-2">Manage customer information</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-customer-button"
              onClick={openCreateDialog}
              className="bg-accent text-accent-foreground hover:bg-accent-hover rounded-sm font-medium"
            >
              <Plus className="w-4 h-4 mr-2" />
              ADD CUSTOMER
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                {editingCustomer ? 'EDIT CUSTOMER' : 'NEW CUSTOMER'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Customer ID</Label>
                <Input
                  data-testid="customer-id-input"
                  value={formData.customer_id}
                  onChange={(e) => setFormData({...formData, customer_id: e.target.value})}
                  disabled={!!editingCustomer}
                  required
                  className="mt-1.5 rounded-none"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Customer Name</Label>
                <Input
                  data-testid="customer-name-input"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                  required
                  className="mt-1.5 rounded-none"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  onClick={() => setDialogOpen(false)}
                  className="bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-sm"
                >
                  CANCEL
                </Button>
                <Button
                  data-testid="submit-customer-button"
                  type="submit"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
                >
                  {editingCustomer ? 'UPDATE' : 'CREATE'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-border shadow-sm">
        {loading ? (
          <div className="p-12 text-center text-gray-700">Loading customers...</div>
        ) : customers.length === 0 ? (
          <div className="p-12 text-center text-gray-700">No customers found. Create your first customer.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full caption-bottom text-sm">
              <thead>
                <tr className="border-b">
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Customer ID</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Customer Name</th>
                  <th className="h-10 px-4 text-center align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer, idx) => (
                  <tr key={customer.customer_id} className="border-b transition-colors hover:bg-muted/50" data-testid={`customer-row-${idx}`}>
                    <td className="p-4 align-middle font-mono text-xs">{customer.customer_id}</td>
                    <td className="p-4 align-middle">{customer.customer_name}</td>
                    <td className="p-4 align-middle">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          data-testid={`edit-customer-${idx}`}
                          onClick={() => handleEdit(customer)}
                          className="p-2 hover:bg-accent/10 hover:text-accent transition-all rounded-sm"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`delete-customer-${idx}`}
                          onClick={() => handleDelete(customer.customer_id)}
                          className="p-2 hover:bg-error/10 hover:text-error transition-all rounded-sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}